/**
 * <s-product-image-zoom> — PDP image zoom preloader + exits.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component:
 *   - warms the selected zoom image on hover/focus so the fullscreen dialog has the
 *     high-resolution image ready when opened;
 *   - closes the zoom when the photo itself is tapped/clicked (photo-viewer convention);
 *   - makes the phone's back gesture/button close an open zoom instead of leaving the
 *     product page (pushes one history entry on open, pops it when closed another way).
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];
  #observer;
  #openDialog = null;
  #closingFromHistory = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    this.addEventListener('click', this.#handleImageClick, opts);
    window.addEventListener('popstate', this.#handlePopState, opts);

    this.#observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.target instanceof HTMLDialogElement) this.#handleDialogToggle(record.target);
      }
    });
    for (const dialog of this.querySelectorAll('dialog')) {
      this.#observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handleImageClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const image = event.target.closest('img[data-zoom-image]');
    image?.closest('dialog')?.close();
  };

  #handleDialogToggle(dialog) {
    if (dialog.open) {
      this.#openDialog = dialog;
      history.pushState({ ...(history.state || {}), productZoom: dialog.id }, '');
      return;
    }
    if (this.#openDialog !== dialog) return;
    this.#openDialog = null;
    if (this.#closingFromHistory) {
      this.#closingFromHistory = false;
      return;
    }
    // Closed by the button, Esc or a photo tap: drop the entry we pushed so Back still means "previous page".
    if (history.state?.productZoom === dialog.id) history.back();
  }

  #handlePopState = () => {
    if (!this.#openDialog) return;
    this.#closingFromHistory = true;
    this.#openDialog.close();
  };

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
